import React from 'react';
import { Table, Tag, Button, Typography } from 'antd';
import { LineChartOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { OperationPeriod } from '../types/operation-period.types';

const { Text } = Typography;

// ========== 定数定義 ==========
const TABLE_CONSTANTS = {
  TABLE_WIDTH_ID: 70,
  TABLE_WIDTH_NAME: 160,
  TABLE_WIDTH_START: 160,
  TABLE_WIDTH_END: 160,
  TABLE_WIDTH_DURATION: 100,
  TABLE_WIDTH_STATUS: 80,
  TABLE_WIDTH_ACTION: 100,
  PAGINATION_PAGE_SIZE_DESKTOP: 10,
  PAGINATION_PAGE_SIZE_MOBILE: 5,
  MINUTES_PER_HOUR: 60,
} as const;

// ========== 型定義 ==========
interface OperationPeriodTableProps {
  readonly operationPeriods: readonly OperationPeriod[];
  readonly loading: boolean;
  readonly isMobile: boolean;
  readonly onShowChart: (operationPeriod: OperationPeriod) => void;
}

/**
 * 日時をフォーマット
 */
function formatDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');

  return `${year}/${month}/${day} ${hour}:${minute}`;
}

/**
 * 稼働時間をフォーマット
 */
function formatDuration(durationMinutes: number | null): string {
  if (durationMinutes === null) {
    return '-';
  }

  const hours = Math.floor(durationMinutes / TABLE_CONSTANTS.MINUTES_PER_HOUR);
  const minutes = durationMinutes % TABLE_CONSTANTS.MINUTES_PER_HOUR;

  if (hours > 0) {
    return `${hours}時間${minutes}分`;
  }

  return `${minutes}分`;
}

/**
 * 運転期間一覧テーブルコンポーネント
 */
export const OperationPeriodTable: React.FC<OperationPeriodTableProps> = ({
  operationPeriods,
  loading,
  isMobile,
  onShowChart,
}) => {
  const columns: ColumnsType<OperationPeriod> = [
    ...(!isMobile
      ? [
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: TABLE_CONSTANTS.TABLE_WIDTH_ID,
          },
        ]
      : []),
    {
      title: '運転名',
      dataIndex: 'operationName',
      key: 'operationName',
      width: TABLE_CONSTANTS.TABLE_WIDTH_NAME,
      render: (operationName: string) => <Text strong>{operationName}</Text>,
    },
    {
      title: '開始時刻',
      dataIndex: 'startedAt',
      key: 'startedAt',
      width: TABLE_CONSTANTS.TABLE_WIDTH_START,
      render: (startedAt: Date) => formatDateTime(startedAt),
    },
    ...(!isMobile
      ? [
          {
            title: '終了時刻',
            dataIndex: 'endedAt',
            key: 'endedAt',
            width: TABLE_CONSTANTS.TABLE_WIDTH_END,
            render: (endedAt: Date | null) =>
              endedAt !== null ? formatDateTime(endedAt) : '-',
          },
        ]
      : []),
    {
      title: '稼働時間',
      dataIndex: 'durationMinutes',
      key: 'durationMinutes',
      width: TABLE_CONSTANTS.TABLE_WIDTH_DURATION,
      render: (durationMinutes: number | null, record: OperationPeriod) => (
        <>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {record.isActive ? (
            <Text type="success">{formatDuration(durationMinutes)} (稼働中)</Text>
          ) : (
            formatDuration(durationMinutes)
          )}
        </>
      ),
    },
    ...(!isMobile
      ? [
          {
            title: '状態',
            dataIndex: 'isActive',
            key: 'isActive',
            width: TABLE_CONSTANTS.TABLE_WIDTH_STATUS,
            render: (isActive: boolean) => (
              <Tag color={isActive ? 'green' : 'default'}>
                {isActive ? '稼働中' : '終了'}
              </Tag>
            ),
          },
        ]
      : []),
    {
      title: 'チャート',
      key: 'action',
      width: TABLE_CONSTANTS.TABLE_WIDTH_ACTION,
      render: (_: unknown, record: OperationPeriod) => (
        <Button
          type="primary"
          icon={<LineChartOutlined />}
          size="small"
          onClick={() => onShowChart(record)}
        >
          {!isMobile && '表示'}
        </Button>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={[...operationPeriods]}
      rowKey="id"
      loading={loading}
      pagination={{
        pageSize: isMobile
          ? TABLE_CONSTANTS.PAGINATION_PAGE_SIZE_MOBILE
          : TABLE_CONSTANTS.PAGINATION_PAGE_SIZE_DESKTOP,
        showTotal: (total) => `合計 ${total} 件`,
      }}
      scroll={isMobile ? { x: true } : undefined}
      size={isMobile ? 'small' : 'middle'}
      locale={{ emptyText: '該当する運転期間がありません' }}
    />
  );
};
